import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listPayments } from "@/features/transcripts/api";
import { AsyncContent } from "@/features/transcripts/components/async-content";
import { useResource } from "@/features/transcripts/hooks/use-resource";
import { exportToCsv } from "@/features/transcripts/lib/export-csv";
import type { PaymentRecord } from "@/features/transcripts/types";
import { useTranscriptsContext } from "@/pages/client/transcripts/transcripts-layout";
import { formatCents } from "@/lib/utils";

const ALL = "all";

export default function ClientPaymentsPage() {
  const { client, forWho, dataVersion } = useTranscriptsContext();
  const [yearFilter, setYearFilter] = useState<string>(ALL);

  const state = useResource<PaymentRecord[]>(
    () => listPayments(client.id, { forWho }),
    [client.id, forWho, dataVersion],
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-foreground mb-2 text-3xl font-bold">Payments</h1>
        <p className="text-muted-foreground max-w-2xl">
          Every payment and credit transaction across this client's tax history.
        </p>
      </div>

      <AsyncContent state={state}>
        {(allPayments) => (
          <PaymentsTable
            allPayments={allPayments}
            clientId={client.id}
            yearFilter={yearFilter}
            onYearFilterChange={setYearFilter}
          />
        )}
      </AsyncContent>
    </div>
  );
}

function PaymentsTable({
  allPayments,
  clientId,
  yearFilter,
  onYearFilterChange,
}: {
  allPayments: PaymentRecord[];
  clientId: string;
  yearFilter: string;
  onYearFilterChange: (v: string) => void;
}) {
  const years = useMemo(
    () => Array.from(new Set(allPayments.map((p) => p.taxYear))).sort((a, b) => b - a),
    [allPayments],
  );
  const payments = useMemo(
    () =>
      yearFilter === ALL
        ? allPayments
        : allPayments.filter((p) => p.taxYear === Number(yearFilter)),
    [allPayments, yearFilter],
  );

  function handleExport() {
    exportToCsv(`${clientId}-payments.csv`, payments, [
      { header: "Date", accessor: (p) => p.date },
      { header: "Tax Year", accessor: (p) => p.taxYear },
      { header: "Form", accessor: (p) => p.form },
      { header: "Code", accessor: (p) => p.code },
      { header: "Explanation", accessor: (p) => p.explanation },
      { header: "Amount", accessor: (p) => formatCents(p.amountCents) },
    ]);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-end gap-2">
        <Select value={yearFilter} onValueChange={onYearFilterChange}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Years</SelectItem>
            {years.map((year) => (
              <SelectItem key={year} value={String(year)}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={payments.length === 0}>
          <Download /> Export CSV
        </Button>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Date</TableHead>
              <TableHead>Tax Year</TableHead>
              <TableHead>Form</TableHead>
              <TableHead>Code — Explanation</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell className="text-muted-foreground">{payment.date}</TableCell>
                <TableCell>{payment.taxYear}</TableCell>
                <TableCell>{payment.form}</TableCell>
                <TableCell>
                  {payment.code} — {payment.explanation}
                </TableCell>
                <TableCell className="text-right">{formatCents(payment.amountCents)}</TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground text-center">
                  No payment transactions for this selection.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
